import { useMemo, useState, useEffect, useRef } from "react";
import tokenService from "services/token.service";
import { useAccount } from "wagmi";

// Global state to track authentication attempts
let globalAuthInProgress = false;
let globalAuthInProgressTimeout: ReturnType<typeof setTimeout> | null = null;

export const setAuthInProgress = (inProgress: boolean) => {
  globalAuthInProgress = inProgress;

  // Auto-reset auth in progress after 30 seconds to prevent permanent blocking
  if (inProgress) {
    if (globalAuthInProgressTimeout) {
      clearTimeout(globalAuthInProgressTimeout);
    }
    globalAuthInProgressTimeout = setTimeout(() => {
      globalAuthInProgress = false;
    }, 30000);
  } else {
    if (globalAuthInProgressTimeout) {
      clearTimeout(globalAuthInProgressTimeout);
      globalAuthInProgressTimeout = null;
    }
  }
};

export const useAuth = () => {
  const { address, isConnected } = useAccount();
  const [hasValidToken, setHasValidToken] = useState(false);
  const lastAddressRef = useRef<string>("");

  useEffect(() => {
    const checkToken = () => {
      // Reset authentication state when address changes
      if (lastAddressRef.current !== address) {
        lastAddressRef.current = address || "";
        setHasValidToken(false);
        // Reset global auth progress when address changes
        setAuthInProgress(false);
      }

      // If no address or not connected, user is not authenticated
      if (!address || !isConnected) {
        setHasValidToken(false);
        return;
      }

      // Check if we have tokens
      const tokens = tokenService.tokens;

      if (tokens) {
        // We have tokens, consider user authenticated
        setHasValidToken(true);
        // Reset auth in progress since we have valid tokens
        setAuthInProgress(false);
      } else {
        // No tokens, user needs to authenticate
        setHasValidToken(false);
      }
    };

    checkToken();

    // Listen for storage changes to update auth state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authTokens" || e.key === null) {
        checkToken();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [address, isConnected]);

  // Return true only if we have address, connection, valid token, and no global auth in progress
  return useMemo(() => {
    return Boolean(
      address && isConnected && hasValidToken && !globalAuthInProgress
    );
  }, [address, isConnected, hasValidToken]);
};
